import { User, UserFormData, ApiResponse, PaginatedResponse } from '../types'
import { getDb } from '../../db/index.js'
import { users, voucherLogs, eq, desc, asc, and, like, sql, or, gte } from '../../db/schema/index.js'

export const userService = {
  async getUsers(params?: {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<ApiResponse<PaginatedResponse<User>>> {
    try {
      const db = getDb()

      // Build query conditions
      let conditions = []
      
      if (params?.search) {
        conditions.push(
          or(
            like(users.namn, `%${params.search}%`),
            like(users.epost, `%${params.search}%`)
          )
        )
      }

      // Get total count
      const countQuery = conditions.length > 0 
        ? db.select({ count: sql<number>`count(*)` }).from(users).where(and(...conditions))
        : db.select({ count: sql<number>`count(*)` }).from(users)
      
      const countResult = await countQuery
      const total = countResult[0]?.count || 0

      // Apply pagination
      const page = params?.page || 1
      const limit = params?.limit || 10
      const offset = (page - 1) * limit

      // Build sort order
      let orderBy = desc(users.created_at) // Default sort
      if (params?.sortBy) {
        // Handle specific sort fields
        switch (params.sortBy) {
          case 'namn':
            orderBy = params.sortOrder === 'desc' ? desc(users.namn) : asc(users.namn)
            break
          case 'epost':
            orderBy = params.sortOrder === 'desc' ? desc(users.epost) : asc(users.epost)
            break
          case 'vouchers':
            orderBy = params.sortOrder === 'desc' ? desc(users.vouchers) : asc(users.vouchers)
            break
          case 'completed_trainings':
            orderBy = params.sortOrder === 'desc' ? desc(users.completed_trainings) : asc(users.completed_trainings)
            break
          case 'created_at':
            orderBy = params.sortOrder === 'desc' ? desc(users.created_at) : asc(users.created_at)
            break
          default:
            // Keep default sort
            break
        }
      }

      // Get paginated results
      let result
      if (conditions.length > 0) {
        result = await db.select()
          .from(users)
          .where(and(...conditions))
          .orderBy(orderBy)
          .limit(limit)
          .offset(offset)
      } else {
        result = await db.select()
          .from(users)
          .orderBy(orderBy)
          .limit(limit)
          .offset(offset)
      }

      return {
        success: true,
        data: {
          data: result as User[],
          total,
          page,
          limit,
        },
        message: 'Users fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch users',
      }
    }
  },

  async getUser(id: number): Promise<ApiResponse<User>> {
    try {
      const db = getDb()
      const result = await db.select().from(users).where(eq(users.id, id)).limit(1)
      const user = result[0]

      if (!user) {
        return {
          success: false,
          message: 'User not found',
        }
      }

      return {
        success: true,
        data: user as User,
        message: 'User fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch user',
      }
    }
  },

  async createUser(data: UserFormData): Promise<ApiResponse<User>> {
    try {
      const db = getDb()

      // Check if name already exists
      const existingResult = await db.select().from(users).where(eq(users.namn, data.namn)).limit(1)
      if (existingResult[0]) {
        return {
          success: false,
          message: 'Name already exists',
        }
      }

      const newUser = {
        namn: data.namn,
        epost: data.epost,
        telefon: data.telefon,
        vouchers: data.vouchers,
        completed_trainings: 0,
        created_at: new Date().toISOString(),
      }

      const result = await db.insert(users).values(newUser).returning()
      const createdUser = result[0]

      return {
        success: true,
        data: createdUser as User,
        message: 'User created successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to create user',
      }
    }
  },

  async updateUser(id: number, data: Partial<UserFormData>): Promise<ApiResponse<User>> {
    try {
      const db = getDb()

      // Check if user exists
      const existingResult = await db.select().from(users).where(eq(users.id, id)).limit(1)
      if (!existingResult[0]) {
        return {
          success: false,
          message: 'User not found',
        }
      }

      // Check if new name conflicts with existing users
      if (data.namn && data.namn !== existingResult[0].namn) {
        const nameConflictResult = await db.select()
          .from(users)
          .where(and(eq(users.namn, data.namn), sql`${users.id} != ${id}`))
          .limit(1)
        
        if (nameConflictResult[0]) {
          return {
            success: false,
            message: 'Name already exists',
          }
        }
      }

      const updateData: any = {}
      if (data.namn !== undefined) updateData.namn = data.namn
      if (data.epost !== undefined) updateData.epost = data.epost
      if (data.telefon !== undefined) updateData.telefon = data.telefon
      if (data.vouchers !== undefined) updateData.vouchers = data.vouchers

      const result = await db.update(users)
        .set(updateData)
        .where(eq(users.id, id))
        .returning()
      
      const updatedUser = result[0]

      return {
        success: true,
        data: updatedUser as User,
        message: 'User updated successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to update user',
      }
    }
  },

  async deleteUser(id: number): Promise<ApiResponse> {
    try {
      const db = getDb()

      // Check if user exists
      const existingResult = await db.select().from(users).where(eq(users.id, id)).limit(1)
      if (!existingResult[0]) {
        return {
          success: false,
          message: 'User not found',
        }
      }

      await db.delete(users).where(eq(users.id, id))

      return {
        success: true,
        message: 'User deleted successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to delete user',
      }
    }
  },

  async updateVouchers(id: number, amount: number, reason?: string): Promise<ApiResponse> {
    try {
      const db = getDb()

      // Check if user exists
      const existingResult = await db.select().from(users).where(eq(users.id, id)).limit(1)
      if (!existingResult[0]) {
        return {
          success: false,
          message: 'User not found',
        }
      }

      const currentVouchers = existingResult[0].vouchers || 0
      const newVouchers = Math.max(0, currentVouchers + amount)

      // Update user vouchers
      await db.update(users)
        .set({ vouchers: newVouchers })
        .where(eq(users.id, id))

      // Log the voucher change
      await db.insert(voucherLogs).values({
        user_id: id,
        change_type: amount > 0 ? 'add' : 'remove',
        amount: Math.abs(amount),
        training_id: null,
        training_title: null,
        training_date: null,
        created_at: new Date().toISOString(),
      })

      return {
        success: true,
        message: `Vouchers updated successfully${reason ? ` (${reason})` : ''}`
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to update vouchers',
      }
    }
  },

  async getUserHistory(id: number): Promise<ApiResponse<any[]>> {
    try {
      const db = getDb()

      // Get voucher logs for the user
      const result = await db.select()
        .from(voucherLogs)
        .where(eq(voucherLogs.user_id, id))
        .orderBy(desc(voucherLogs.created_at))
        .limit(20)

      // Format the history data
      const history = result.map((log: any) => ({
        id: log.id,
        action: log.change_type === 'add' ? 'voucher_added' : 'voucher_used',
        amount: log.change_type === 'add' ? log.amount : -log.amount,
        reason: log.training_title || 'Manual adjustment',
        timestamp: log.created_at,
      }))

      return {
        success: true,
        data: history,
        message: 'User history fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch user history',
      }
    }
  },

  async getUserStats(): Promise<ApiResponse<{
    total_users: number
    active_users: number
    new_users_this_month: number
  }>> {
    try {
      const db = getDb()

      // Get total users count
      const totalResult = await db.select({ count: sql<number>`count(*)` }).from(users)
      const totalUsers = totalResult[0]?.count || 0

      // Get active users (with vouchers > 0) count
      const activeResult = await db.select({ count: sql<number>`count(*)` })
        .from(users)
        .where(sql`${users.vouchers} > 0`)
      const activeUsers = activeResult[0]?.count || 0

      // Get new users this month
      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const newUsersResult = await db.select({ count: sql<number>`count(*)` })
        .from(users)
        .where(gte(users.created_at, startOfMonth.toISOString()))
      const newUsersThisMonth = newUsersResult[0]?.count || 0

      return {
        success: true,
        data: {
          total_users: totalUsers,
          active_users: activeUsers,
          new_users_this_month: newUsersThisMonth,
        },
        message: 'User stats fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch user stats',
      }
    }
  },
}
